import { useCallback, useEffect, useRef, useState } from 'react';
import { authApi } from '../services/authApi';
import { httpStatus, summaryErrorCopy } from '../lib/cogniSummary';
import type { CogniSummary } from '../types';

export type CogniSummaryState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; summary: CogniSummary; open: boolean }
  | { phase: 'error'; message: string; retry: boolean };

/**
 * On-request Cogni summary state machine. Every async continuation is guarded
 * by a generation token so a response for a superseded search never lands.
 */
export function useCogniSummary(args: {
  cacheKey: string | undefined;
  resetKey: string;
  streamLoading: boolean;
}): { state: CogniSummaryState; generate: () => void; hide: () => void } {
  const { cacheKey, resetKey, streamLoading } = args;
  const [state, setState] = useState<CogniSummaryState>({ phase: 'idle' });
  const genRef = useRef(0);
  const latestKeyRef = useRef(cacheKey);
  latestKeyRef.current = cacheKey;
  const stateRef = useRef(state);
  stateRef.current = state;

  const reset = useCallback(() => {
    genRef.current += 1;
    setState({ phase: 'idle' });
  }, []);

  // resetKey change (new Ask / query / filters)
  const prevResetKey = useRef(resetKey);
  useEffect(() => {
    if (prevResetKey.current !== resetKey) {
      prevResetKey.current = resetKey;
      reset();
    }
  }, [resetKey, reset]);

  // streamLoading rising edge
  const prevLoading = useRef(streamLoading);
  useEffect(() => {
    if (!prevLoading.current && streamLoading) reset();
    prevLoading.current = streamLoading;
  }, [streamLoading, reset]);

  // cacheKey changes to a different defined value; a transient undefined is ignored
  const lastDefinedKey = useRef(cacheKey);
  useEffect(() => {
    if (cacheKey === undefined) return;
    if (lastDefinedKey.current !== undefined && lastDefinedKey.current !== cacheKey) reset();
    lastDefinedKey.current = cacheKey;
  }, [cacheKey, reset]);

  const generate = useCallback(() => {
    const cur = stateRef.current;
    if (cur.phase === 'loading') return;
    if (cur.phase === 'ready') {
      if (!cur.open) setState({ ...cur, open: true });
      return;
    }
    const key = latestKeyRef.current;
    if (!key) return;
    const token = ++genRef.current;
    setState({ phase: 'loading' });
    authApi
      .generateCogniSummary({ cache_key: key })
      .then((res) => {
        if (token !== genRef.current) return;
        setState({ phase: 'ready', summary: res.summary, open: true });
      })
      .catch((err: unknown) => {
        if (token !== genRef.current) return;
        const { message, retry } = summaryErrorCopy(httpStatus(err));
        setState({ phase: 'error', message, retry });
      });
  }, []);

  const hide = useCallback(() => {
    setState((s) => (s.phase === 'ready' ? { ...s, open: false } : s));
  }, []);

  return { state, generate, hide };
}
